import {
  ALMResourceContentTypes,
  type IALMResourceData,
  type ILearningObjectData,
  LearningObjectEnrollmentTypes,
  LearningObjectStates,
  LearningObjectTypes
} from '@/entities';

import { translateMetadata } from '@/utils';
import type { ILearningObjectResponse, IncludedType, LoEnrollmentType, LoType } from '@/services';
import { guards } from '@/services';
import type { IResourceResponse } from '@/services/prime/resource';
import { mapPrimeInstance } from '@/mappers/instance';
import { mapPrimeEnrollment } from '@/mappers/enrollment';

export const mapPrimeLearningObject = (
  data: ILearningObjectResponse,
  included?: IncludedType[]
): ILearningObjectData => {
  const learningObjects = data.relationships?.subLOs?.data
    .map(
      (item) =>
        included?.filter(guards.isLearningObject).find((lo) => lo.id === item.id) || ({} as ILearningObjectResponse)
    )
    .filter((lo) => lo.id);
  const learningObjectInstances = included
    ?.filter(guards.isInstance)
    .filter((i) => i.relationships?.learningObject?.data.id === data.id);
  const learningObjectInstanceEnrollment = included
    ?.filter(guards.isEnrollment)
    .find((e) => e.relationships?.learningObject?.data.id === data.id);

  const resources = data.relationships?.supplementaryResources?.data
    .map((item) => included?.filter(guards.isResourceData).find((r) => r.id === item.id) || ({} as IResourceResponse))
    .filter((item) => {
      if (item.id) return item;
    });

  return {
    id: data?.id,
    image: data.attributes?.imageUrl || '',
    name: translateMetadata(data.attributes.localizedMetadata)?.name || '',
    duration: data.attributes.duration || 0,
    dateCreated: data.attributes.dateCreated || '',
    enrollmentType: toEnrollmentType(data.attributes.enrollmentType),
    description: translateMetadata(data.attributes.localizedMetadata)?.overview || '',
    type: toLearningObjectType(data.attributes.loType),
    isExternal: data.attributes.isExternal || false,
    state: data.attributes.state as LearningObjectStates,
    isOrdered: data.attributes?.isSubLoOrderEnforced || false,
    unenrollmentAllowed: data.attributes.unenrollmentAllowed || false,
    file: data.attributes.url || '',
    // modules: learningObjectResources?.map((m) => this.moduleMap.toData(m, included)),
    courses: learningObjects?.map((lo) => mapPrimeLearningObject(lo, included)),
    instances: learningObjectInstances?.map((i) => mapPrimeInstance(i, included)),
    enrollment: learningObjectInstanceEnrollment
      ? mapPrimeEnrollment(learningObjectInstanceEnrollment, included)
      : undefined,
    resources: resources?.map((item) => toResources(item)),
    tags: data.attributes.tags || []
  };
};

const toLearningObjectType = (loType: LoType | undefined): LearningObjectTypes => {
  if (loType == 'learningProgram') return LearningObjectTypes.LearningProgram;
  if (loType == 'jobAid') return LearningObjectTypes.JobAid;
  if (loType == 'certification') return LearningObjectTypes.Certification;
  else return LearningObjectTypes.Course;
};

const toEnrollmentType = (LoEnrollmentType: LoEnrollmentType | undefined): LearningObjectEnrollmentTypes => {
  if (LoEnrollmentType == 'Admin Enroll') return LearningObjectEnrollmentTypes.AdminEnroll;
  if (LoEnrollmentType == 'Admin Nominated') return LearningObjectEnrollmentTypes.AdminNominated;
  if (LoEnrollmentType == 'Manager Approval') return LearningObjectEnrollmentTypes.ManagerApproval;
  if (LoEnrollmentType == 'Manager Nominated') return LearningObjectEnrollmentTypes.ManagerNominated;
  return LearningObjectEnrollmentTypes.SelfEnroll;
};

const toResources = (resource: IResourceResponse): IALMResourceData => {
  return {
    id: resource.id,
    name: resource.attributes?.name || '',
    contentType: toContentType(resource.attributes.contentType),
    location: resource.attributes.location
  };
};

const toContentType = (contentType: ALMResourceContentTypes): ALMResourceContentTypes => {
  if (contentType == 'Attachment') return ALMResourceContentTypes.Attachment;
  else return ALMResourceContentTypes.Other;
};
